import { TransferDialog } from '@/components/header/modals/transfer-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFetchAccount } from '@/features/account/api/get-account';
import { useOrders } from '@/features/account/api/get-orders';
import OpenOrdersTable from '@/features/account/components/open-orders-table';
import OrdersHistoryTable from '@/features/account/components/orders-history-table';
import TransfersTable from '@/features/account/components/transfers-table';
import { getAccountPnl, getAccountValue } from '@/features/account/utils/math';
import PortfolioChart from '@/features/portfolio/components/chart';
import LockedCollateralAssetForm from '@/features/portfolio/components/locked-collateral-asset-form';
import PortfolioStats from '@/features/portfolio/components/stats';
import { PositionsTableContainer } from '@/features/trade/components';
import { useUnifiedAccount } from '@/hooks';
import { cn } from '@/lib/utils';
import { useAccountStore, useCurrentAccount } from '@/store/use-account-store';
import { useMarketStores } from '@/store/use-markets-store';
import { useOrdersStore } from '@/store/use-orders-store';
import {
  OrderHistoryFilterType,
  OrderStatus,
  OrderType,
  TableTabType,
  TransferType,
} from '@/types/enums';
import { formatBigInt, formatNumber } from '@/utils/value-format';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export default function PortfolioPage() {
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('view') as TableTabType;
  const isDefaultTabCorrect =
    !!defaultTab && Object.values(TableTabType).includes(defaultTab);
  const [tableTab, setTableTab] = useState<TableTabType>(
    isDefaultTabCorrect ? defaultTab : TableTabType.POSITIONS,
  );
  const [orderHistoryFilter, setOrderHistoryFilter] =
    useState<OrderHistoryFilterType>(OrderHistoryFilterType.ALL);

  const account = useCurrentAccount();
  const markets = useMarketStores((state) => ({
    marketData: state.marketData,
    marketSpec: state.marketSpec,
  }));

  const { isPending } = useFetchAccount();

  const accountStats = useMemo(() => {
    if (isPending)
      return {
        value: undefined,
        pnl: undefined,
      };
    return {
      value: getAccountValue(account, markets),
      pnl: getAccountPnl(account, markets),
    };
  }, [isPending, markets]);

  useOrders({ status: OrderStatus.NEW });

  const { address } = useUnifiedAccount();

  const openOrders = useOrdersStore((state) => {
    const { orders, newOrderIds } = state.getOrders(address);

    return Array.from(newOrderIds, (id) => orders[id]).filter(
      (order) => order.orderType !== OrderType.MARKET,
    );
  });

  const { isMobileConnected } = useAccountStore();

  useEffect(() => {
    if (!isDefaultTabCorrect) return;
    setTableTab(defaultTab);
  }, [defaultTab]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-col gap-2 px-6 pb-4 pt-5 md:hidden">
        <h1 className="font-mono uppercase text-vestgrey-600">Account Value</h1>
        <span className="font-mono text-4xl font-medium text-foreground">
          {formatNumber(accountStats.value?.decimal, {
            digits: 2,
          })}
        </span>
        <span
          className={cn(
            'font-mono text-lg font-medium',
            isPending && 'opacity-0',
            accountStats.pnl && accountStats.pnl.bigint > 0n
              ? 'text-green'
              : 'text-red',
          )}
        >
          {formatNumber(accountStats.pnl?.decimal, {
            digits: 2,
            showChange: true,
          })}{' '}
          {accountStats.pnl &&
            account.collateral > 0n &&
            `(${formatBigInt((accountStats.pnl.bigint * 10000n) / account.collateral, 2, { digits: 2, style: 'percent', showChange: false })})`}
        </span>
      </div>
      <div className="grid md:grid-cols-4">
        <div className="hidden md:grid">
          <PortfolioStats />
        </div>
        <PortfolioChart />
      </div>
      <Tabs
        value={tableTab}
        onValueChange={(value) => setTableTab(value as TableTabType)}
        defaultValue="positions"
        className="flex w-full flex-1 flex-col"
      >
        {/* to make right side of tabs foreground div */}
        <div className="flex">
          <TabsList className="no-scrollbar w-full justify-start overflow-x-auto overflow-y-hidden md:w-max md:min-w-max">
            <TabsTrigger
              className="h-14 min-w-40 flex-1 gap-2 max-md:px-6 md:min-w-0 md:flex-none"
              value="positions"
            >
              POSITIONS
              <span className="text-vestgrey-100">
                {Object.keys(account.positions).length > 0
                  ? `(${Object.keys(account.positions).length})`
                  : ''}
              </span>
            </TabsTrigger>
            <TabsTrigger
              className="h-14 min-w-40 flex-1 gap-2 max-md:px-6 md:min-w-0 md:flex-none"
              value="open-orders"
            >
              OPEN ORDERS
              <span className="text-vestgrey-100">
                {' '}
                {openOrders.length > 0 ? `(${openOrders.length})` : ''}
              </span>
            </TabsTrigger>
            <TabsTrigger
              className="h-14 min-w-40 flex-1 max-md:px-6 md:min-w-0 md:flex-none"
              value="order-history"
            >
              ORDER HISTORY
            </TabsTrigger>
            <TabsTrigger
              className="h-14 min-w-40 flex-1 max-md:px-6 md:min-w-0 md:flex-none"
              value="transfers"
            >
              TRANSFERS
            </TabsTrigger>
            <TabsTrigger
              className="h-14 min-w-max flex-1 max-md:px-6 md:min-w-0 md:flex-none"
              value="locked-asset-collateral"
            >
              LOCKED ASSET COLLATERAL
            </TabsTrigger>
          </TabsList>
          {/* to make right side of tabs foreground div */}
          <div className="hidden w-full items-center justify-end gap-4 border-y border-border bg-background md:flex">
            {tableTab === TableTabType.ORDER_HISTORY && (
              <Select
                onValueChange={(value) =>
                  setOrderHistoryFilter(value as OrderHistoryFilterType)
                }
              >
                <SelectTrigger
                  id="filter"
                  className="h-full max-w-32 rounded-none border-y-0 border-r-0"
                >
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={OrderHistoryFilterType.ALL}>
                    All
                  </SelectItem>
                  <SelectItem value={OrderHistoryFilterType.FILLED}>
                    Filled
                  </SelectItem>
                  <SelectItem value={OrderHistoryFilterType.REJECTED}>
                    Rejected
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
        <TabsContent className="relative flex-1" value="positions">
          <div className="absolute inset-0 h-full w-full pb-12">
            <PositionsTableContainer />
          </div>
        </TabsContent>
        <TabsContent className="relative flex-1" value="open-orders">
          <div className="absolute inset-0 h-full w-full pb-12">
            <OpenOrdersTable />
          </div>
        </TabsContent>
        <TabsContent className="relative flex-1" value="order-history">
          <div className="absolute inset-0 h-full w-full pb-12">
            <OrdersHistoryTable filter={orderHistoryFilter} />
          </div>
        </TabsContent>
        <TabsContent className="relative flex-1" value="transfers">
          <div className="absolute inset-0 h-full w-full pb-12">
            <TransfersTable />
          </div>
        </TabsContent>
        <TabsContent
          className="relative flex-1"
          value="locked-asset-collateral"
        >
          <div className="flex h-full items-center justify-center pt-6 md:pb-6">
            <LockedCollateralAssetForm />
          </div>
        </TabsContent>
      </Tabs>
      {isMobileConnected ? (
        <div className="py-4 text-center text-lg text-vestgrey-600">
          Deposits and withdrawals are disabled.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 p-6 md:hidden">
          {/* <Button size="xl" className="font-normal" variant="green">
          Deposit
        </Button>
        <Button size="xl" className="font-normal" variant="red">
          Withdraw
        </Button> */}

          <TransferDialog orderType={TransferType.DEPOSIT} size="xl" />
          <TransferDialog
            orderType={TransferType.WITHDRAW}
            size="xl"
            className="h-14"
          />
        </div>
      )}
    </div>
  );
}
